import React, { useState, useContext, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Formik, FieldArray, Field } from 'formik';
import * as Yup from 'yup';
import { AuthContext } from '../../context/AuthContext';
import reportService from '../../services/reportService';
import projectService from '../../services/projectService';
import { formatHoursToHoursMinutes, formatDecimalHours } from '../../utils/timeUtils';

const SubmitReport = () => {
  const { currentUser } = useContext(AuthContext);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  
  // Function to normalize hours input - handles minutes over 59 (rolling over to next hour)
  const normalizeHoursInput = (value) => {
    if (!value || value === '') return '0.00';
    
    // Don't allow negative values
    if (parseFloat(value) < 0) return '0.00';
    
    // If it has a decimal part
    if (value.toString().includes('.')) {
      const parts = value.toString().split('.');
      let hours = parseInt(parts[0], 10) || 0;
      let minutes = parts[1];
      
      // Handle the minutes part
      if (minutes.length === 1) {
        // Treat single digit as tens of minutes (e.g., 0.4 = 0.40 = 40 minutes)
        minutes = parseInt(minutes, 10) * 10 || 0;
      } else {
        // Parse regular minutes
        minutes = parseInt(minutes, 10) || 0;
      }
      
      // Handle rollover if minutes >= 60
      if (minutes >= 60) {
        hours += Math.floor(minutes / 60);
        minutes = minutes % 60;
      }
      
      // Format to hours.minutes (with leading zeros for minutes if needed)
      return `${hours}.${minutes.toString().padStart(2, '0')}`;
    }
    
    // No decimal, just whole hours
    return `${parseInt(value, 10) || 0}.00`;
  };
  
  // Get current date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];
  
  // Fetch projects when component mounts
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        // Get projects assigned to the current developer
        const assignedProjects = await projectService.getMyProjects();
        
        // If developer has no assigned projects, get all projects as fallback
        if (assignedProjects.length === 0) {
          const allProjects = await projectService.getAllProjects();
          setProjects(allProjects);
        } else {
          setProjects(assignedProjects);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchProjects();
  }, []);
  
  // Validation schema
  const validationSchema = Yup.object({
    date: Yup.date().required('Date is required'),
    tasks: Yup.string().required('Summary of tasks is required'),
    task_details: Yup.array().of(
      Yup.object().shape({
        description: Yup.string().required('Task description is required'),
        hours: Yup.number()
          .required('Hours are required')
          .positive('Hours must be positive')
          .max(24, 'Hours cannot exceed 24'),
        project_id: Yup.number().required('Project selection is required')
      })
    ).min(1, 'At least one task is required')
  });
  
  // Initial form values
  const initialValues = {
    date: today,
    tasks: '',
    blockers: '',
    tomorrow_plan: '',
    task_details: [{ description: '', hours: '', project_id: '' }]
  };
  
  // Pre-select project if there's only one available
  useEffect(() => {
    if (projects.length === 1 && !isLoading) {
      console.log('Only one project available, pre-selecting:', projects[0].name);
      // We need to set this in a separate effect to avoid issues with Formik
      const formElement = document.querySelector('select[name="project_id"]');
      if (formElement) {
        formElement.value = projects[0].id;
      }
    }
  }, [projects, isLoading]);
  
  // Calculate total hours from task details
  const calculateTotalHours = (taskDetails) => {
    let totalHours = 0;
    let totalMinutes = 0;
    
    taskDetails.forEach(task => {
      if (!task.hours) return;
      
      const hoursStr = task.hours.toString();
      
      if (hoursStr.includes('.')) {
        const parts = hoursStr.split('.');
        const wholeHours = parseInt(parts[0], 10) || 0;
        const decimalPart = parts[1];
        
        // Add the whole hours to total
        totalHours += wholeHours;
        
        // Always treat decimal part as direct minutes (e.g., 1.40 means 1 hour 40 minutes)
        // If it's a single digit, treat as tens of minutes (e.g., 1.4 means 1 hour 40 minutes)
        if (decimalPart.length === 1) {
          totalMinutes += parseInt(decimalPart, 10) * 10 || 0;
        } else {
          totalMinutes += parseInt(decimalPart, 10) || 0;
        }
      } else {
        // No decimal, just whole hours
        totalHours += parseInt(hoursStr, 10) || 0;
      }
    });
    
    // Handle minute rollover to hours (60 minutes = 1 hour)
    if (totalMinutes >= 60) {
      totalHours += Math.floor(totalMinutes / 60);
      totalMinutes = totalMinutes % 60;
    }
    
    // Format as decimal hours with minutes as the decimal part (e.g., 3.05 for 3 hours 5 minutes)
    if (totalMinutes === 0) {
      return totalHours.toFixed(2);
    } else {
      const formattedMinutes = totalMinutes.toString().padStart(2, '0');
      return `${totalHours}.${formattedMinutes}`;
    }
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitError('');
    setSubmitSuccess(false);
    
    try {
      console.log('Submitting report with values:', values);
      
      // Validate task details
      if (!values.task_details || values.task_details.length === 0) {
        throw new Error('Please add at least one task');
      }
      
      // Check for empty task descriptions, hours, or project selection
      const invalidTasks = values.task_details.filter(
        task => !task.description || !task.hours || isNaN(parseFloat(task.hours)) || !task.project_id
      );
      
      if (invalidTasks.length > 0) {
        throw new Error('Please fill in all task descriptions, hours, and project selections');
      }
      
      // Calculate total hours from task details
      const totalHours = calculateTotalHours(values.task_details);
      console.log('Calculated total hours:', totalHours);
      
      if (totalHours <= 0) {
        throw new Error('Total hours must be greater than zero');
      }
      
      // Add hours_worked to the values
      const reportData = {
        ...values,
        hours_worked: totalHours
      };
      
      // Ensure project_id is a number
      if (reportData.project_id) {
        reportData.project_id = Number(reportData.project_id);
      }
      
      // Ensure date is in the correct format
      if (!reportData.date) {
        reportData.date = today;
      }
      
      console.log('Sending report data:', reportData);
      
      const response = await reportService.createReport(reportData);
      console.log('Report submission successful:', response);
      
      setSubmitSuccess(true);
      resetForm();
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate('/reports');
      }, 2000);
    } catch (error) {
      console.error('Error submitting report:', error);
      
      // Handle specific error messages
      if (error.response) {
        console.error('Error response:', error.response.data);
        // Handle specific backend errors
        if (error.response.data.msg === 'You have already submitted a report for this date') {
          setSubmitError('You have already submitted a report for today. Please edit your existing report if needed.');
        } else if (error.response.data.msg === 'The selected project does not exist') {
          setSubmitError('The selected project is no longer available. Please choose another project.');
        } else if (error.response.status === 401) {
          setSubmitError('Your session has expired. Please log in again.');
          // Redirect to login after a short delay
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        } else {
          setSubmitError(error.response.data.msg || 'Failed to submit report');
        }
      } else if (error.request) {
        console.error('No response received from server');
        setSubmitError('No response received from server. Please check your connection and try again.');
      } else {
        console.error('Error message:', error.message);
        setSubmitError(error.message || 'An error occurred while submitting your report. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="page-fade-in">
    <Container>
      <Row className="justify-content-center">
        <Col md={8}>
          <Card style={{ border: '1px solid #EEEEEE', borderRadius: 14 }}>
            <Card.Header as="h4" style={{
              background: 'linear-gradient(135deg, #E8E0F0, #D6E6F5)',
              borderBottom: 'none', borderRadius: '14px 14px 0 0',
              padding: '0.9rem 1.25rem',
              fontWeight: 700,
              color: '#7C6FA0'
            }}>
              Submit Daily Report
            </Card.Header>
            <Card.Body>
              {submitError && (
                <Alert variant="danger">{submitError}</Alert>
              )}
              
              {submitSuccess && (
                <Alert variant="success">
                  Report submitted successfully! Redirecting to your reports...
                </Alert>
              )}
              
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date</Form.Label>
                      <Form.Control
                        type="text"
                        value={new Date().toLocaleDateString()}
                        disabled
                        readOnly
                      />
                      <Form.Control
                        type="hidden"
                        name="date"
                        value={values.date}
                      />
                    </Form.Group>
                    

                    
                    <Form.Group className="mb-3">
                      <Form.Label>Summary of Tasks</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="tasks"
                        value={values.tasks}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.tasks && errors.tasks}
                        placeholder="Provide a brief summary of your work today"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.tasks}
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Form.Label className="mb-0">Task Details</Form.Label>
                        <div>
                          <span className="me-2">
                            Total Hours: <strong>{formatHoursToHoursMinutes(calculateTotalHours(values.task_details))}</strong>
                            <div className="text-muted small">({formatDecimalHours(calculateTotalHours(values.task_details))} hours)</div>
                          </span>
                        </div>
                      </div>
                      
                      <FieldArray name="task_details">
                        {({ push, remove }) => (
                          <div>
                            {values.task_details.map((task, index) => (
                              <div key={index} className="mb-3">
                                <Row>
                                  <Col xs={12} md={4}>
                                    <Form.Group className="mb-2">
                                      <Form.Label className="small">Project</Form.Label>
                                      <Form.Select
                                        name={`task_details.${index}.project_id`}
                                        value={task.project_id}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={
                                          touched.task_details && 
                                          touched.task_details[index] && 
                                          touched.task_details[index].project_id && 
                                          errors.task_details && 
                                          errors.task_details[index] && 
                                          errors.task_details[index].project_id
                                        }
                                        disabled={isLoading}
                                      >
                                        <option value="">Select project</option>
                                        {projects.map(project => (
                                          <option key={project.id} value={project.id}>
                                            {project.name}
                                          </option>
                                        ))}
                                      </Form.Select>
                                      {touched.task_details && 
                                       touched.task_details[index] && 
                                       touched.task_details[index].project_id && 
                                       errors.task_details && 
                                       errors.task_details[index] && 
                                       errors.task_details[index].project_id && (
                                        <Form.Control.Feedback type="invalid">
                                          {errors.task_details[index].project_id}
                                        </Form.Control.Feedback>
                                      )}
                                    </Form.Group>
                                  </Col>
                                  <Col xs={12} md={5}>
                                    <Form.Group className="mb-2">
                                      <Form.Label className="small">Task Description</Form.Label>
                                      <Form.Control
                                        name={`task_details.${index}.description`}
                                        value={task.description}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={
                                          touched.task_details && 
                                          touched.task_details[index] && 
                                          touched.task_details[index].description && 
                                          errors.task_details && 
                                          errors.task_details[index] && 
                                          errors.task_details[index].description
                                        }
                                        placeholder="Task description"
                                      />
                                      {touched.task_details && 
                                       touched.task_details[index] && 
                                       touched.task_details[index].description && 
                                       errors.task_details && 
                                       errors.task_details[index] && 
                                       errors.task_details[index].description && (
                                        <Form.Control.Feedback type="invalid">
                                          {errors.task_details[index].description}
                                        </Form.Control.Feedback>
                                      )}
                                    </Form.Group>
                                  </Col>
                                  <Col xs={12} md={2}>
                                    <Form.Group className="mb-2">
                                      <Form.Label className="small">Hours</Form.Label>
                                      <InputGroup>
                                        <Form.Control
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          name={`task_details.${index}.hours`}
                                          value={task.hours}
                                          onChange={(e) => {
                                            // Get the value entered
                                            let rawVal = e.target.value;
                                            
                                            // Don't allow negative values
                                            if (rawVal < 0) rawVal = 0;
                                            
                                            // Process the raw value first
                                            handleChange(e);
                                          }}
                                          onBlur={(e) => {
                                            // On blur, normalize the hours format
                                            const normalizedVal = normalizeHoursInput(e.target.value);
                                            
                                            // Set the normalized value
                                            const fakeEvent = {
                                              target: {
                                                name: e.target.name,
                                                value: normalizedVal
                                              }
                                            };
                                            
                                            // Update the form with the normalized value
                                            handleChange(fakeEvent);
                                            handleBlur(e);
                                          }}
                                          isInvalid={
                                            touched.task_details && 
                                            touched.task_details[index] && 
                                            touched.task_details[index].hours && 
                                            errors.task_details && 
                                            errors.task_details[index] && 
                                            errors.task_details[index].hours
                                          }
                                          placeholder="Hours"
                                        />
                                        <InputGroup.Text>hrs</InputGroup.Text>
                                        {touched.task_details && 
                                         touched.task_details[index] && 
                                         touched.task_details[index].hours && 
                                         errors.task_details && 
                                         errors.task_details[index] && 
                                         errors.task_details[index].hours && (
                                          <Form.Control.Feedback type="invalid">
                                            {errors.task_details[index].hours}
                                          </Form.Control.Feedback>
                                        )}
                                      </InputGroup>
                                    </Form.Group>
                                  </Col>
                                  <Col xs={1} className="d-flex align-items-center">
                                    {values.task_details.length > 1 && (
                                      <Button 
                                        variant="outline-danger" 
                                        size="sm"
                                        onClick={() => remove(index)}
                                      >
                                        <i className="bi bi-trash"></i>
                                      </Button>
                                    )}
                                  </Col>
                                </Row>
                              </div>
                            ))}
                            
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => push({ description: '', hours: '', project_id: '' })}
                              className="mt-2"
                            >
                              <i className="bi bi-plus"></i> Add Task
                            </Button>
                          </div>
                        )}
                      </FieldArray>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Blockers (Optional)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="blockers"
                        value={values.blockers}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Describe any blockers or challenges you faced"
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Tomorrow's Plan (Optional)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="tomorrow_plan"
                        value={values.tomorrow_plan}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="What do you plan to work on tomorrow?"
                      />
                    </Form.Group>
                    
                    <div className="d-flex justify-content-between">
                      <Button
                        variant="secondary"
                        onClick={() => navigate('/dashboard')}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={isSubmitting || submitSuccess}
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Report'}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
    </div>
  );
};

export default SubmitReport;
